import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, KeyRound } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';
import flexcoaLogo from '../../assets/images/flexcoa-logo-condensed-green.png';
import { validateCaregiverPin, PIN_REQUIREMENTS_HINT } from './pinValidation';

/**
 * Caregiver invite acceptance page (FECOA-1320 email-invitation flow).
 *
 * Read the single-use token from the URL (?token=...), let the caregiver
 * set their initial PIN, and route into the dashboard.
 */
const CaregiverInviteAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = searchParams.get('token') || '';
    setToken(t);
    if (!t) {
      setError('No invitation token found in the URL. Please use the link from your email.');
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) return;
    const policyError = validateCaregiverPin(newPin);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPin !== confirmPin) {
      setError('PINs do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await caregiverService.acceptInvite({ token, new_pin: newPin });
      toast.success('Welcome — your account is ready.');
      navigate('/caregiver/dashboard');
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail;
      setError(detail || 'Could not accept invitation.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    'w-full pl-10 pr-3 py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-700 rounded-md text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="min-h-screen pt-8 bg-gray-50 dark:bg-slate-950 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={flexcoaLogo} alt="flexCOA" className="h-10 w-auto mb-3" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Set Up Your Account</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 text-center">
            Choose a PIN to use whenever you sign in.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white dark:bg-slate-900/80 border border-gray-200 dark:border-slate-800 rounded-xl shadow-lg p-6 space-y-4"
        >
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              New PIN
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                autoComplete="new-password"
                inputMode="numeric"
                className={inputCls}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{PIN_REQUIREMENTS_HINT}</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              Confirm PIN
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="password"
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value)}
                autoComplete="new-password"
                inputMode="numeric"
                className={inputCls}
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10 border border-rose-300 dark:border-rose-500/30 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !token}
            className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-medium rounded-md text-sm flex items-center justify-center"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CaregiverInviteAccept;
